<template>
    <base-dialog :loading="loading" :value="dialog" :width="540" :title="$t('common.upload_links')"
        @input="val => { if (!val) hide() }" @close="hide">
        <p class="video-form-desc">
            {{ $t('common.upload_links_subtitle') }}<br><br>
            <span v-html="$t('common.upload_links_help')" />
        </p>
        <div v-for="(link, i) in links" :key="i" class="video-field-row">
            <div>
                <div class="video-field-label">{{ $t('common.name') }} {{ i + 1 }}</div>
                <input class="video-input" :placeholder="$t('common.write_here')" :value="link.name"
                    @input="link.name = $event.target.value" />
            </div>
            <div>
                <div class="video-field-label">{{ $t('common.link') }} {{ i + 1 }}</div>
                <input class="video-input" :placeholder="$t('common.write_here')" :value="link.videoId"
                    @input="link.videoId = $event.target.value"
                    @blur="link.videoId = getYouTubeVideoId(link.videoId)" />
            </div>
            <v-btn icon class="video-field-btn yt" :disabled="!link.videoId" @click="goToVideo(link.videoId)"
                :title="$t('exercises.watch_video')">
                <v-icon size="16">mdi-youtube</v-icon>
            </v-btn>
            <v-btn icon class="video-field-btn del" @click="deleteVideo(i)" :title="$t('common.delete')">
                <v-icon size="16">$delete</v-icon>
            </v-btn>
        </div>
        <v-btn icon class="add-field-btn" @click="addVideo()" :title="$t('common.add')">
            <v-icon size="20">mdi-plus</v-icon>
        </v-btn>

        <template v-slot:footer>
            <v-btn block depressed x-large color="primary" class="footer-btn black--text" @click="save"
                :loading="loading">
                {{ $t('common.save') }}
            </v-btn>
        </template>
    </base-dialog>
</template>

<script>


import { Browser } from '@capacitor/browser'
import { db } from "@/utils/Firebase";
import { sendMessage } from "@/utils/Messages";
import BaseDialog from "@/components/common/BaseDialog";

export default {
    name: "YoutubeUploadLinksDialog",
    components: { BaseDialog },
    data(vm) {
        return {
            dialog: false,
            loading: false,
            linksTemplate: [{ name: '', videoId: '' }],
            links: [],
            linksDefault: null,
        }
    },
    computed: {
        coachId(vm = this) {
            return vm.currentUser.uid || null
        },
    },
    methods: {
        show() {
            const vm = this
            vm.setDefaults()
            vm.dialog = true
        },
        hide() {
            const vm = this
            vm.dialog = false
            vm.reset()
        },
        reset() {
            const vm = this
            vm.links = vm.linksTemplate
        },
        getYouTubeVideoId(url) {
            const pattern = /^(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
            const match = url?.match(pattern)
            return match?.[1] ? match[1] : null
        },
        setDefaults() {
            const vm = this
            vm.links = [...vm.account?.links || vm.linksTemplate]
            vm.linksDefault = JSON.parse(JSON.stringify(vm.links))
        },
        async goToVideo(videoId) {
            const vm = this
            if (!videoId) return
            const url = 'https://www.youtube.com/watch?v=' + videoId
            try {
                await Browser.open({ url, presentationStyle: 'popover' })
            } catch (e) { /* ignore */ }
        },
        deleteVideo(i) {
            const vm = this;
            if (vm.links.length === 1) return;
            vm.links.splice(i, 1)
        },
        addVideo() {
            const vm = this;
            vm.links.push({...vm.linksTemplate})
        },
        hasLinksChanged() {
            const vm = this;
            if (vm.links.length !== vm.linksDefault.length) return true;
            return vm.links.some((link, index) => {
                const defaultLink = vm.linksDefault[index];
                return link.name !== defaultLink.name || link.videoId !== defaultLink.videoId;
            });
        },
        async save() {
            const vm = this
            vm.loading = true

            await db.collection('users').doc(vm.currentUser.uid).update({
                links: vm.links.filter(link => !!link.name && !!link.videoId)
            })

            if (vm.hasLinksChanged()) {
                let studentsRef = await db.collection('users')
                    .where('studentSubscription.coachId', '==', vm.coachId)
                    .where('studentSubscription.paused', '==', false)
                    .get()
                await Promise.all(studentsRef.docs.map(student =>
                    student.id ? vm.sendMessageToStudent(student.id) : Promise.resolve()
                ))
            }

            vm.toast(vm.$t('common.links_saved'))
            vm.loading = false
            vm.hide()
        },
        async sendMessageToStudent(studentId) {
            const vm = this

            let message = vm.$t('common.new_video_upload')

            let coachId = vm.coachId

            await sendMessage({ message, from: coachId }, coachId, studentId)
        },
    },

}

</script>

<style scoped>
.video-form-desc {
    font-size: 13px;
    color: var(--v-txtSecondary-base);
    line-height: 1.6;
    margin-bottom: 20px;
}

.video-form-desc >>> strong {
    color: var(--v-txtPrimary-base);
}

.video-field-row {
    display: grid;
    grid-template-columns: 1fr 1fr 36px 36px;
    gap: 8px;
    align-items: end;
    margin-bottom: 10px;
}

.video-field-label {
    font-size: 11px;
    font-weight: 600;
    color: var(--v-txtMuted-base);
    text-transform: uppercase;
    letter-spacing: 0.06em;
    margin-bottom: 4px;
}

.video-input {
    width: 100%;
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline2-base);
    border-radius: 7px;
    padding: 8px 12px;
    font-size: 13px;
    color: var(--v-txtPrimary-base);
    outline: none;
    transition: border-color 0.15s;
}

.video-input::placeholder {
    color: var(--v-txtMuted-base);
}

.video-input:focus {
    border-color: var(--v-accent-base);
}

.theme--light .video-input {
    background: var(--v-bg-base);
}

.video-field-btn {
    width: 36px !important;
    height: 36px !important;
    border-radius: 7px !important;
    border: 1px solid var(--v-outline2-base) !important;
    background: var(--v-surface2-base) !important;
    transition: background 0.15s, color 0.15s;
}

.video-field-btn:hover {
    background: var(--v-outline2-base) !important;
}

.video-field-btn >>> .v-icon {
    color: var(--v-txtSecondary-base);
}

.video-field-btn.yt:hover >>> .v-icon {
    color: #FF0000;
}

.video-field-btn.del:hover >>> .v-icon {
    color: var(--v-error-base);
}

.add-field-btn {
    width: 36px !important;
    height: 36px !important;
    background: var(--v-txtPrimary-base) !important;
    margin: 4px 0 0 auto;
    display: flex !important;
    transition: opacity 0.15s;
}

.add-field-btn:hover {
    opacity: 0.85;
}

.add-field-btn >>> .v-icon {
    color: var(--v-bg-base);
}

.footer-btn {
    border-radius: 0 !important;
    height: 52px !important;
    font-weight: 700;
}

</style>